import {
  REALTIME_ORIGIN_HEADER,
  REALTIME_PATH,
  REALTIME_PROTOCOL_VERSION,
  parseRealtimeServerMessage,
} from "./protocol";
import type {
  RealtimeEventEnvelope,
  RealtimeModule,
  RealtimeServerMessage,
} from "./protocol";

export type RealtimeSignal =
  | { type: "sync" }
  | RealtimeEventEnvelope;

type Listener = (signal: RealtimeSignal) => void;

const listeners = new Map<RealtimeModule, Set<Listener>>();
const clientId = crypto.randomUUID();
const reconnectDelays = [1_000, 2_000, 4_000, 8_000, 15_000, 30_000];
let socket: WebSocket | null = null;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let heartbeatTimer: ReturnType<typeof setInterval> | null = null;
let reconnectAttempt = 0;
let lastSequence = 0;
let intentionallyClosed = false;

function activeModules() {
  return [...listeners.entries()]
    .filter(([, moduleListeners]) => moduleListeners.size > 0)
    .map(([module]) => module);
}

function notify(module: RealtimeModule, signal: RealtimeSignal) {
  for (const listener of listeners.get(module) ?? []) listener(signal);
}

function sendSubscriptions() {
  if (socket?.readyState !== WebSocket.OPEN) return;
  socket.send(JSON.stringify({
    version: REALTIME_PROTOCOL_VERSION,
    type: "subscribe",
    modules: activeModules(),
  }));
}

function clearHeartbeat() {
  if (heartbeatTimer) clearInterval(heartbeatTimer);
  heartbeatTimer = null;
}

function scheduleReconnect() {
  if (intentionallyClosed || reconnectTimer || activeModules().length === 0) return;
  const delay = reconnectDelays[Math.min(reconnectAttempt, reconnectDelays.length - 1)]!;
  reconnectAttempt += 1;
  reconnectTimer = setTimeout(() => {
    reconnectTimer = null;
    connect();
  }, delay);
}

function handleMessage(message: RealtimeServerMessage) {
  if (message.type === "ready") {
    reconnectAttempt = 0;
    return;
  }
  if (message.type === "subscribed") {
    lastSequence = message.sequence;
    for (const module of activeModules()) notify(module, { type: "sync" });
    return;
  }
  if (message.type !== "event") return;
  if (message.sequence <= lastSequence) return;
  lastSequence = message.sequence;
  if (message.originClientId === clientId) return;
  notify(message.module, message);
}

function connect() {
  if (typeof window === "undefined" || activeModules().length === 0) return;
  if (socket && (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING)) return;
  intentionallyClosed = false;
  const scheme = window.location.protocol === "https:" ? "wss:" : "ws:";
  const nextSocket = new WebSocket(`${scheme}//${window.location.host}${REALTIME_PATH}`);
  socket = nextSocket;

  nextSocket.addEventListener("open", () => {
    sendSubscriptions();
    clearHeartbeat();
    heartbeatTimer = setInterval(() => {
      if (socket?.readyState === WebSocket.OPEN) {
        socket.send(JSON.stringify({
          version: REALTIME_PROTOCOL_VERSION,
          type: "ping",
          sentAt: Date.now(),
        }));
      }
    }, 20_000);
  });
  nextSocket.addEventListener("message", event => {
    if (typeof event.data !== "string") return;
    const message = parseRealtimeServerMessage(event.data);
    if (message) handleMessage(message);
  });
  nextSocket.addEventListener("close", () => {
    if (socket !== nextSocket) return;
    socket = null;
    clearHeartbeat();
    scheduleReconnect();
  });
  nextSocket.addEventListener("error", () => nextSocket.close());
}

export function subscribeRealtime(module: RealtimeModule, listener: Listener) {
  let moduleListeners = listeners.get(module);
  if (!moduleListeners) {
    moduleListeners = new Set();
    listeners.set(module, moduleListeners);
  }
  moduleListeners.add(listener);
  connect();
  sendSubscriptions();

  return () => {
    moduleListeners?.delete(listener);
    if (moduleListeners?.size === 0) listeners.delete(module);
    if (activeModules().length > 0) {
      sendSubscriptions();
      return;
    }
    intentionallyClosed = true;
    if (reconnectTimer) clearTimeout(reconnectTimer);
    reconnectTimer = null;
    clearHeartbeat();
    socket?.close(1000, "No active subscriptions");
    socket = null;
  };
}

export function realtimeFetch(input: RequestInfo | URL, init: RequestInit) {
  const headers = new Headers(init.headers);
  headers.set(REALTIME_ORIGIN_HEADER, clientId);
  return fetch(input, { ...init, headers });
}
